import { Box, Typography } from '@mui/material';
import { Check } from 'lucide-react';

export interface StepperHeaderStep {
  key: string;
  label: string;
}

export interface StepperHeaderProps {
  steps: StepperHeaderStep[];
  /** Index of the step currently being displayed. Out-of-range (e.g. equal
   * to steps.length) means nothing is active — used once everything is done. */
  activeIndex: number;
  /** Index the user has truly progressed past — may be ahead of activeIndex
   * when they've clicked back to review an earlier, already-completed step. */
  completedIndex: number;
  /** Only completed steps (index < completedIndex) are clickable. */
  onStepClick?: (step: StepperHeaderStep, index: number) => void;
  /** Circles + progress line only, no per-segment labels — the current
   * step's name runs as a single line below instead. Three full labels
   * side by side don't fit a phone-width card. */
  compact?: boolean;
}

export const StepperHeader = ({
  steps,
  activeIndex,
  completedIndex,
  onStepClick,
  compact,
}: StepperHeaderProps) => {
  const currentLabel = activeIndex >= 0 && activeIndex < steps.length ? steps[activeIndex].label : null;

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'stretch', width: '100%' }}>
        {steps.map((step, index) => {
          const active = index === activeIndex;
          const done = index < completedIndex;
          const clickable = done;
          return (
            <Box
              key={step.key}
              onClick={clickable ? () => onStepClick?.(step, index) : undefined}
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                pb: '12px',
                cursor: clickable ? 'pointer' : 'default',
                borderBottom: '2px solid',
                borderBottomColor: done ? '#C0FF02' : 'var(--tx-line)',
              }}>
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: (done && !active) || active ? 'var(--tx-lime)' : 'transparent',
                  border: '1.5px solid',
                  borderColor: (done && !active) || active ? '#C0FF02' : 'var(--tx-line)',
                  color: (done && !active) || active ? 'var(--tx-text)' : '#77736E',
                  fontSize: 10,
                  fontWeight: 600,
                }}>
                {done && !active ? <Check size={10} strokeWidth={3} /> : index + 1}
              </Box>
              {!compact && (
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: active ? 'var(--tx-text)' : done ? 'var(--tx-muted)' : 'var(--tx-faint)',
                  }}>
                  {step.label}
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>
      {compact && currentLabel && (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-text)', mt: '10px' }}>
          Step {activeIndex + 1} of {steps.length} &middot; {currentLabel}
        </Typography>
      )}
    </Box>
  );
};
